import {
    TEMPLATE_CONFIG_CACHE_KEY,
    templateConfigStorageLocal,
    templateModStorage,
    type TemplateConfigStorage,
} from "./templateConfigStorage";
import { HttpTiendaTemplate } from "../http/httpTiendaTemplate";
import { normalizarContenidoTemplate } from "./templateConfigNormalizer";
import type { TiendaBaseConfigDTO } from "./types";

export interface TemplateConfigInicial {
    config: TiendaBaseConfigDTO;
    bitPreview: boolean;
    bitContenidoMigrado: boolean;
    vchCacheKey: string;
    storage: TemplateConfigStorage;
}

export async function cargarTemplateConfigInicial(
    storage: TemplateConfigStorage = templateConfigStorageLocal,
): Promise<TemplateConfigInicial> {
    const vchCacheKey = TEMPLATE_CONFIG_CACHE_KEY;
    const bitContentEditMode = detectarContentEditMode();

    // Preview de template: ?template=<id> → carga el estilo del catálogo y el
    // contenido actual del tenant. No se cachea porque el contenido administrado
    // debe reflejarse siempre al volver a abrir la vista previa o el editor.
    const vchTemplatePreview =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("template")
            : null;

    if (vchTemplatePreview) {
        // Estilo del template del catálogo + contenido real del tenant:
        // así el preview muestra "tus textos con el estilo de este template",
        // que es exactamente lo que pasaría si lo activaras.
        const [templateBase, contenidoActual] = await Promise.all([
            HttpTiendaTemplate.getTemplateBase(vchTemplatePreview),
            HttpTiendaTemplate.getContenido(),
        ]);
        if (templateBase && contenidoActual) {
            const contenidoNormalizado = normalizarContenidoTemplate(contenidoActual);
            const config: TiendaBaseConfigDTO = {
                ...templateBase,
                ...contenidoNormalizado.contenido,
                bitActiva: true,
            };
            return {
                config,
                bitPreview: true,
                bitContenidoMigrado: contenidoNormalizado.bitContenidoMigrado,
                vchCacheKey,
                storage,
            };
        }
    }

    // En modo edición siempre cargar desde la API: el refresh post-render está deshabilitado
    // en este modo, así que el cache puede estar desactualizado.
    if (bitContentEditMode) {
        const [estilo, contenido] = await Promise.all([
            HttpTiendaTemplate.getEstilo(),
            HttpTiendaTemplate.getContenido(),
        ]);
        if (estilo && contenido) {
            const contenidoNormalizado = normalizarContenidoTemplate(contenido);
            const config: TiendaBaseConfigDTO = {
                ...estilo,
                ...contenidoNormalizado.contenido,
                bitActiva: true,
            };
            return {
                config,
                bitPreview: false,
                bitContenidoMigrado: contenidoNormalizado.bitContenidoMigrado,
                vchCacheKey,
                storage,
            };
        }
    }

    // Si ya hay config en localStorage → render inmediato (el useEffect post-render sincroniza con API)
    const configCache = await storage.obtener(vchCacheKey);
    if (configCache?.bitActiva != null) {
        const contenidoNormalizado = normalizarContenidoTemplate(configCache);
        const config = {
            ...configCache,
            ...contenidoNormalizado.contenido,
        };
        if (contenidoNormalizado.bitContenidoMigrado) {
            await storage.guardar(vchCacheKey, config);
        }
        return {
            config,
            bitPreview: false,
            bitContenidoMigrado: contenidoNormalizado.bitContenidoMigrado,
            vchCacheKey,
            storage,
        };
    }

    // Primera carga sin cache → traer de la API
    const [estilo, contenido] = await Promise.all([
        HttpTiendaTemplate.getEstilo(),
        HttpTiendaTemplate.getContenido(),
    ]);

    if (estilo && contenido) {
        const contenidoNormalizado = normalizarContenidoTemplate(contenido);
        const config: TiendaBaseConfigDTO = {
            ...estilo,
            ...contenidoNormalizado.contenido,
            bitActiva: true,
        };
        await storage.guardar(vchCacheKey, config);
        return {
            config,
            bitPreview: false,
            bitContenidoMigrado: contenidoNormalizado.bitContenidoMigrado,
            vchCacheKey,
            storage,
        };
    }

    throw new Error("No se pudo cargar la configuración de la tienda.");
}

/** Llamada post-render: checkeo liviano de timestamps, descarga solo lo que cambió */
export async function refrescarTemplateConfigDesdeAPI(
    inicial: TemplateConfigInicial,
): Promise<TiendaBaseConfigDTO | null> {
    const activo = await HttpTiendaTemplate.getActivo();
    if (!activo) return null;

    const storedModEstilo = templateModStorage.getModEstilo();
    const storedModContenido = templateModStorage.getModContenido();
    const estiloChanged = activo.dtmFechaModEstilo !== storedModEstilo;
    const contenidoChanged = activo.dtmFechaModContenido !== storedModContenido;

    if (!estiloChanged && !contenidoChanged) return null;

    const [nuevoEstilo, nuevoContenido] = await Promise.all([
        estiloChanged ? HttpTiendaTemplate.getEstilo() : Promise.resolve(null),
        contenidoChanged ? HttpTiendaTemplate.getContenido() : Promise.resolve(null),
    ]);

    const contenidoNormalizado = nuevoContenido
        ? normalizarContenidoTemplate(nuevoContenido)
        : null;
    const configActualizada: TiendaBaseConfigDTO = {
        ...inicial.config,
        ...(nuevoEstilo ?? {}),
        ...(contenidoNormalizado?.contenido ?? {}),
        bitActiva: true,
    };

    await inicial.storage.guardar(inicial.vchCacheKey, configActualizada);
    if (nuevoEstilo) templateModStorage.setModEstilo(activo.dtmFechaModEstilo);
    if (nuevoContenido) templateModStorage.setModContenido(activo.dtmFechaModContenido);

    return configActualizada;
}

export function detectarContentEditMode(): boolean {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("contentEditMode") === "true";
}
